import * as React from 'react';
import { Button, useDataProvider, useNotify } from 'react-admin';
import { Typography, Box, Tabs, Tab, Paper, Chip, TextField as MuiTextField } from '@material-ui/core';
import { errorMessage, getObject, post } from './noticeApi';

const MAX_LENGTH = 1000;
const MAX_SIGN_OFF = 150;

const previewTabs = [
    { key: 'issued', label: 'Notice' },
    { key: 'revised', label: 'Revised notice' },
    { key: 'rescinded', label: 'Rescinded' },
];

/**
 * The production's message and sign off for notice emails, with a live preview
 * of the emails. Self-contained, add <NoticeSettingsPanel /> to the settings page.
 *
 * The message is added as an extra paragraph after the standard text on the
 * notice and the revised notice. The sign off ends all three emails. Whether notices are sent automatically is a
 * back office setting, shown here but not editable.
 */
export const NoticeSettingsPanel = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [loaded, setLoaded] = React.useState(false);
    const [failed, setFailed] = React.useState(null);
    const [enabled, setEnabled] = React.useState(false);
    const [saved, setSaved] = React.useState('');
    const [message, setMessage] = React.useState('');
    const [savedSignOff, setSavedSignOff] = React.useState('');
    const [signOff, setSignOff] = React.useState('');
    const [previews, setPreviews] = React.useState(null);
    const [tab, setTab] = React.useState(0);
    const [saving, setSaving] = React.useState(false);
    const firstPreview = React.useRef(true);

    React.useEffect(() => {
        let cancelled = false;

        getObject(dataProvider, 'notices/settings')
            .then(data => {
                if (cancelled) return;
                setEnabled(!!data.notices_enabled);
                setSaved(data.notice_custom_message ?? '');
                setMessage(data.notice_custom_message ?? '');
                setSavedSignOff(data.notice_sign_off ?? '');
                setSignOff(data.notice_sign_off ?? '');
                setPreviews(data.previews);
                setLoaded(true);
            })
            .catch(err => {
                console.error('Notice settings could not be loaded', err);
                if (!cancelled) setFailed([err?.status, err?.body?.message ?? err?.message].filter(Boolean).join(': ') || 'unknown error');
            });

        return () => { cancelled = true; };
    }, [dataProvider]);

    // Refresh the preview shortly after typing stops. The first run is skipped,
    // the settings response already has the previews for the saved message.
    React.useEffect(() => {
        if (!loaded) return undefined;

        if (firstPreview.current) {
            firstPreview.current = false;
            return undefined;
        }

        let cancelled = false;
        const timer = setTimeout(() => {
            getObject(dataProvider, 'notices/settings/preview', { notice_custom_message: message, notice_sign_off: signOff })
                .then(data => { if (!cancelled) setPreviews(data.previews); })
                .catch(() => { /* keep the last preview */ });
        }, 400);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [dataProvider, loaded, message, signOff]);

    const save = () => {
        setSaving(true);

        // create() does not return the server's response, so the saved settings are fetched
        post(dataProvider, 'notices/settings', { notice_custom_message: message, notice_sign_off: signOff })
            .then(() => getObject(dataProvider, 'notices/settings'))
            .then(data => {
                setSaved(data.notice_custom_message ?? '');
                setMessage(data.notice_custom_message ?? '');
                setSavedSignOff(data.notice_sign_off ?? '');
                setSignOff(data.notice_sign_off ?? '');
                setPreviews(data.previews);
                notify('Notice settings saved', 'success');
            })
            .catch(err => notify(errorMessage(err, 'Error saving the notice settings'), 'error'))
            .finally(() => setSaving(false));
    };

    if (failed) {
        return <Typography variant="body2" color="textSecondary">Notice settings could not be loaded ({failed}).</Typography>;
    }

    if (!loaded) {
        return <Typography variant="body2">Loading...</Typography>;
    }

    const preview = previews?.[previewTabs[tab].key];

    return (
        <Box mb={3}>
            <Typography variant="h6" gutterBottom>End of engagement notices</Typography>

            <Box mb={2} display="flex" alignItems="center" style={{ gap: 8 }}>
                <Chip size="small" color={enabled ? 'primary' : 'default'} label={enabled ? 'Sent automatically' : 'Automatic notices are off'} />
                <Typography variant="caption" color="textSecondary">
                    {enabled
                        ? 'Crew are emailed when their end date is within their notice period.'
                        : 'Switched on for a production by Film Beans. Notice can still be given by hand.'}
                </Typography>
            </Box>

            <MuiTextField
                id="notice-custom-message"
                label="Your message (optional)"
                value={message}
                onChange={e => setMessage(e.target.value)}
                fullWidth
                multiline
                minRows={4}
                variant="outlined"
                inputProps={{ maxLength: MAX_LENGTH }}
                helperText={`Added as a paragraph after the standard text in the notice and revised notice emails, not the rescinded email. Leave a blank line to start a new paragraph. ${message.length}/${MAX_LENGTH}`}
            />

            <Box mt={2}>
                <MuiTextField
                    id="notice-sign-off"
                    label="Sign off (optional)"
                    value={signOff}
                    onChange={e => setSignOff(e.target.value.replace(/\s+/g, ' ').replace(/^\s/, ''))}
                    fullWidth
                    variant="outlined"
                    placeholder="Jane Doe, Line Producer"
                    inputProps={{ maxLength: MAX_SIGN_OFF }}
                    helperText={`Ends all three emails, after "Kind regards,". One line, for example a name and job title. ${signOff.length}/${MAX_SIGN_OFF}`}
                />
            </Box>

            <Box mt={1} mb={3}>
                <Button label="Save" onClick={save} disabled={saving || (message === saved && signOff === savedSignOff)} />
            </Box>

            <Typography variant="subtitle2" gutterBottom>Preview</Typography>
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                Shows your message as typed, with example names and dates.
            </Typography>
            <Tabs value={tab} onChange={(e, value) => setTab(value)} indicatorColor="primary" textColor="primary">
                {previewTabs.map(t => <Tab key={t.key} label={t.label} />)}
            </Tabs>

            {preview && (
                <Paper variant="outlined" style={{ padding: 16, marginTop: 8, maxWidth: 640 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Subject</Typography>
                    <Typography variant="body2" gutterBottom>{preview.subject}</Typography>
                    <Typography variant="h6" gutterBottom>{preview.headline}</Typography>
                    {preview.lines.map((line, index) => (
                        <Typography key={index} variant="body2" paragraph>{line}</Typography>
                    ))}
                </Paper>
            )}
        </Box>
    );
};

export default NoticeSettingsPanel;
